import { useCallback, useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Icon, NoSeBorra, ZonaPeligro } from "@/components/ds";
import { ArrowLeft, Loader2, Save } from "@/components/ds/icons";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { formatNombreArea, ordenarAreas } from "@/lib/area";
import { MaquinariaForm } from "@/pages/recursos/MaquinariaForm";
import { RecursoDocumentosPanel } from "@/components/recurso-documentos/RecursoDocumentosPanel";
import type { Maquinaria } from "@/types/maquinaria";

/**
 * Ficha de la maquinaria — encabezado de identidad + tabs Información y
 * Documentos. Espejo estructural de `FichaVehiculo.tsx`, y por el mismo motivo:
 * acá viven las acciones que el listado dejó fuera (editar, activar/desactivar
 * y mover a la papelera), porque decidir sobre una máquina sin ver antes su
 * documentación es justo lo que conviene evitar.
 *
 * `GET /maquinarias/{id}` existe vía `Route::apiResource('maquinarias', ...)`
 * con route-model-binding numérico estándar — se usa directo.
 */
export function FichaMaquinaria() {
  const [, params] = useRoute("/maquinaria/:id/ficha");
  const [, navigate] = useLocation();
  const id = params?.id;

  const [maquinaria, setMaquinaria] = useState<Maquinaria | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [aEliminar, setAEliminar] = useState(false);
  const [eliminando, setEliminando] = useState(false);

  const cargar = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setNotFound(false);
    try {
      const res = await api.get<{ data: Maquinaria }>(`/maquinarias/${id}`);
      setMaquinaria(res.data);
    } catch {
      // 404 (inexistente o de otra empresa) y cualquier otro error de carga
      // caen en el mismo estado "no encontrada" — sin fuga de información,
      // mismo criterio que `RecursoDocumentoContext::resolver()` (fc-api).
      setNotFound(true);
      setMaquinaria(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Mover a la papelera vive SÓLO acá y en la fila del listado: desde la ficha
  // se decide con la documentación delante, que es el punto de tenerla.
  const moverAPapelera = async () => {
    if (!maquinaria) return;
    setEliminando(true);
    try {
      await api.delete(`/maquinarias/${maquinaria.id}`);
      toast.success(`"${maquinaria.nombre}" movida a la papelera. Sus documentos se conservan.`);
      navigate("/recursos/maquinaria");
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Esta maquinaria ya está en la papelera.");
      } else {
        toast.error("No se pudo mover la maquinaria a la papelera.");
      }
      setEliminando(false);
    }
  };

  if (!id) return null;

  const areasTexto =
    maquinaria && (maquinaria.areas ?? []).length > 0
      ? ordenarAreas(maquinaria.areas ?? [])
          .map(formatNombreArea)
          .join(" · ")
      : "Sin área";

  return (
    // `max-w-[900px]` sale del prototipo (`Maquinaria.dc.html`, contenedor de la
    // ficha de equipo) y no es cosmético: sin tope, la fila de un documento
    // estira su primera columna hasta ~1000px y deja el nombre en un extremo y
    // su estado en el otro, con el medio vacío. Mismo criterio que la ficha del
    // vehículo.
    <div className="max-w-[900px] space-y-5">
      <button
        type="button"
        onClick={() => navigate("/recursos/maquinaria")}
        className="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1.5 text-[12.5px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver a Maquinaria
      </button>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 pt-6">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </CardContent>
        </Card>
      ) : notFound || !maquinaria ? (
        <Card>
          <CardContent className="text-muted-foreground py-12 text-center text-sm">
            No se encontró la maquinaria solicitada, o no pertenece a tu empresa.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Encabezado de identidad */}
          <div className="flex flex-wrap items-start gap-3.5">
            <span className="bg-primary-100 text-primary-hover flex size-[46px] shrink-0 items-center justify-center rounded-xl">
              <Icon name="precision_manufacturing" size={24} />
            </span>
            <div className="min-w-[220px] flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-2xl font-bold tracking-[-.025em]">{maquinaria.nombre}</h1>
                {maquinaria.eliminado_at ? (
                  <Badge variant="muted" dot>
                    En papelera
                  </Badge>
                ) : (
                  <Badge variant={maquinaria.activo ? "success" : "neutral"} dot>
                    {maquinaria.activo ? "Activa" : "Inactiva"}
                  </Badge>
                )}
              </div>
              <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2 text-[12.5px]">
                <span>
                  {[maquinaria.marca, maquinaria.modelo].filter(Boolean).join(" ") || "—"}
                </span>
                <span className="text-border-strong">·</span>
                <span>{maquinaria.tipo ?? "Sin tipo"}</span>
                {maquinaria.codigo && (
                  <>
                    <span className="text-border-strong">·</span>
                    <span className="fc-num">{maquinaria.codigo}</span>
                  </>
                )}
                <span className="text-border-strong">·</span>
                <span>{areasTexto}</span>
              </div>
            </div>
          </div>

          <Tabs defaultValue="info" variant="ficha" className="space-y-4">
            <TabsList className="max-w-full flex-wrap">
              <TabsTrigger value="info">Información</TabsTrigger>
              <TabsTrigger value="documentos">Documentos</TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="space-y-3">
              {/* Sin Card envolvente: `layout="secciones"` ya trae sus propias
                  tarjetas, y anidarlas daría un borde dentro de otro borde. */}
              <MaquinariaForm
                mode="edit"
                maquinaria={maquinaria}
                autoFocusFirst={false}
                layout="secciones"
                formClassName="space-y-3"
                onSaved={(actualizada) => setMaquinaria(actualizada)}
                renderFooter={({ isSubmitting }) => (
                  <div className="flex justify-end">
                    <Button type="submit" disabled={isSubmitting} className="gap-2">
                      {isSubmitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      Guardar cambios
                    </Button>
                  </div>
                )}
              />

              <BloqueEnOperacion maquinaria={maquinaria} onCambiada={setMaquinaria} />

              {/* La zona de peligro va fuera del <form> — ver ZonaPeligro. */}
              <ZonaPeligro
                titulo="Mover a la papelera"
                descripcion={
                  <>
                    Sale del inventario, pero <NoSeBorra />: sus documentos y los checklists
                    firmados se conservan. Puedes recuperarla desde Maquinaria › Papelera, o
                    eliminarla definitivamente desde ahí.
                  </>
                }
                accion="Mover a la papelera"
                onAccion={() => setAEliminar(true)}
              />
            </TabsContent>

            <TabsContent value="documentos" className="space-y-4">
              <RecursoDocumentosPanel ambito="maquinaria" documentableId={maquinaria.id} />
            </TabsContent>
          </Tabs>

          <AlertDialog open={aEliminar} onOpenChange={(open) => !open && setAEliminar(false)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Mover a papelera</AlertDialogTitle>
                <AlertDialogDescription>
                  ¿Quieres mover <strong>{maquinaria.nombre}</strong> a la papelera? Sale del
                  inventario y de las asignaciones, pero puedes restaurarla cuando quieras desde la
                  pestaña Papelera del listado.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={eliminando}>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    moverAPapelera();
                  }}
                  disabled={eliminando}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {eliminando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mover a papelera"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}

/**
 * "En operación" — interruptor de efecto INMEDIATO, fuera del formulario.
 *
 * Gemelo de `BloqueEnOperacion` en `FichaVehiculo`, por el mismo motivo:
 * activar o desactivar una máquina es una sola decisión, y dejarlo dentro del
 * `<form>` dejó de funcionar cuando Guardar se movió adentro de la tarjeta
 * Identificación — el interruptor quedaba POR DEBAJO del botón que lo
 * guardaba, o sea que moverlo no hacía nada visible y el cambio se perdía al
 * salir.
 *
 * Va por `PATCH /maquinarias/{id}/estado` y no por el `PUT` del formulario: el
 * endpoint sólo acepta `activo`, así que no puede arrastrar a la base las
 * ediciones a medio hacer que el usuario tenga en pantalla.
 */
function BloqueEnOperacion({
  maquinaria,
  onCambiada,
}: {
  maquinaria: Maquinaria;
  onCambiada: (maquinaria: Maquinaria) => void;
}) {
  const [guardando, setGuardando] = useState(false);

  const cambiar = async (activo: boolean) => {
    setGuardando(true);
    // Optimista: el interruptor tiene que responder al toque. Si la API falla
    // se revierte y se avisa — dejarlo "pensando" convierte una acción de un
    // click en una pantalla trabada.
    onCambiada({ ...maquinaria, activo });
    try {
      const res = await api.patch<{ data: Maquinaria }>(
        `/maquinarias/${maquinaria.id}/estado`,
        { activo },
      );
      onCambiada(res.data);
      toast.success(
        activo
          ? `${maquinaria.nombre} quedó en operación.`
          : `${maquinaria.nombre} quedó fuera de operación: sale de las asignaciones y de los checklists nuevos.`,
      );
    } catch {
      onCambiada(maquinaria);
      toast.error("No se pudo cambiar el estado de la maquinaria.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <section className="bg-card flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3.5">
      <div className="flex min-w-[220px] flex-1 flex-col gap-0.5">
        <Label htmlFor="maquinaria-en-operacion" className="text-[13px] font-semibold">
          {maquinaria.activo ? "En operación" : "Fuera de operación"}
        </Label>
        <p className="text-soft text-[11.5px] leading-[1.5]">
          {maquinaria.activo
            ? "Aparece en las asignaciones y en los checklists de terreno. Si la desactivas sigue en el inventario con sus documentos."
            : "No aparece en asignaciones ni en checklists nuevos. Puedes reactivarla cuando vuelva a operar."}
        </p>
      </div>
      <Switch
        id="maquinaria-en-operacion"
        checked={maquinaria.activo}
        onCheckedChange={cambiar}
        disabled={guardando}
      />
    </section>
  );
}
